import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Fuse from 'fuse.js';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import Select, { type GroupBase } from 'react-select';
import { Link } from 'react-router-dom';
import { ConfirmDialog, Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { IconGripVertical, IconSortLevel, IconTrash } from '../components/icons';
import { selectClassNames } from '../components/selectStyles';
import { useTweenedNumber } from '../hooks/useTweenedNumber';
import { ApiError } from '../lib/api';
import { pushToast } from '../lib/events';
import { totalLegionLevel } from '../lib/legion';
import {
  classDisplayName,
  groupClassesByBranch,
  sortWorldsHeroicFirst,
  useClasses,
  useWorlds,
  type ClassDto,
  type WorldDto,
} from '../lib/reference';
import {
  avatarProxyPath,
  useAccounts,
  useCreateAccount,
  useCreateCharacter,
  useDeleteAccount,
  useDeleteCharacter,
  useReorderAccounts,
  useRefreshAvatar,
  useReorderCharacters,
  useUpdateAccount,
  useUpdateCharacter,
  type AccountDto,
  type CharacterDto,
} from '../lib/roster';

const LEVEL_MIN = 1;
const LEVEL_MAX = 300;

/** Lazy: the screenshot-import modal (WP9) pulls in tesseract.js, which must never land in the
 *  main bundle — `React.lazy` + a dynamic `import()` keeps both out until the button is clicked. */
const ScreenshotImportModal = lazy(() =>
  import('../components/characters/ScreenshotImportModal').then((m) => ({
    default: m.ScreenshotImportModal,
  })),
);

// -------------------------------------------------------------------------------------------
// Avatar (REV4, proxied same-origin since REV12) — a fixed-size circle so rows never reflow
// between an avatar and no avatar; a null `avatarUrl` (never looked up a match) or a failed
// image load (e.g. a 404 from an expired upstream URL) both fall back to the character's
// initial rather than leaving a blank gap.
// -------------------------------------------------------------------------------------------

function CharacterAvatar({
  name,
  accountId,
  characterId,
  avatarUrl,
  size,
}: {
  name: string;
  accountId: number;
  characterId: number;
  avatarUrl: string | null;
  size: 'row' | 'modal';
}) {
  const dimension = size === 'row' ? 'size-10' : 'size-12';
  // `avatarUrl` only marks "this character has an avatar" (REV12) — the actual `<img>` src is
  // always our own proxy route, never the upstream URL directly.
  const src = avatarUrl != null ? avatarProxyPath(accountId, characterId) : null;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <div
      className={`flex ${dimension} shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-elevated text-sm font-semibold text-text-faint`}
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{name.trim().slice(0, 1).toUpperCase() || '?'}</span>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// Account form
// -------------------------------------------------------------------------------------------

interface WorldOption {
  value: string;
  label: string;
}

function AccountFormModal({ account, onClose }: { account: AccountDto | null; onClose: () => void }) {
  const { data: worlds } = useWorlds();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const [worldSlug, setWorldSlug] = useState(account?.worldSlug ?? '');
  const [label, setLabel] = useState(account?.label ?? '');
  const [error, setError] = useState<string | null>(null);

  const pending = createAccount.isPending || updateAccount.isPending;

  // Heroic worlds first (the product's primary audience), each type as its own option group.
  const worldOptionGroups: readonly GroupBase<WorldOption>[] = useMemo(() => {
    if (!worlds) {
      return [];
    }
    const sorted = sortWorldsHeroicFirst(worlds);
    const toOption = (world: WorldDto) => ({
      value: world.slug,
      label: world.name + (!world.active ? ' (merged)' : ''),
    });
    return [
      { label: 'Heroic', options: sorted.filter((w) => w.worldType === 'HEROIC').map(toOption) },
      { label: 'Interactive', options: sorted.filter((w) => w.worldType !== 'HEROIC').map(toOption) },
    ].filter((group) => group.options.length > 0);
  }, [worlds]);
  const flatWorldOptions = useMemo(
    () => worldOptionGroups.flatMap((group) => group.options),
    [worldOptionGroups],
  );
  const selectedWorldOption =
    flatWorldOptions.find((option) => option.value === worldSlug) ?? flatWorldOptions[0] ?? null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const selectedWorld = worldSlug || flatWorldOptions[0]?.value || '';
    if (!selectedWorld) {
      setError('Pick a world first.');
      return;
    }
    // The label is genuinely optional server-side (WP6 D-API4): a blank box is sent through as-is
    // and RosterService defaults it to the world's display name, so the client no longer needs to
    // compute that default itself.
    const rawLabel = label.trim();

    const onError = (err: unknown) => {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not save this account.');
    };

    if (account) {
      updateAccount.mutate(
        { accountId: account.id, input: { worldSlug: selectedWorld, label: rawLabel } },
        { onSuccess: onClose, onError },
      );
    } else {
      createAccount.mutate(
        { worldSlug: selectedWorld, label: rawLabel },
        { onSuccess: onClose, onError },
      );
    }
  };

  return (
    <Modal title={account ? 'Edit account' : 'Create account'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="account-world" className="mb-1 block text-sm font-medium text-text-muted">
            World
          </label>
          <Select<WorldOption, false, GroupBase<WorldOption>>
            inputId="account-world"
            instanceId="account-world"
            unstyled
            options={worldOptionGroups}
            value={selectedWorldOption}
            onChange={(option) => setWorldSlug(option?.value ?? '')}
            placeholder="Pick a world…"
            noOptionsMessage={() => 'No matching worlds'}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 60 }) }}
            classNames={selectClassNames}
          />
        </div>
        <div>
          <label htmlFor="account-label" className="mb-1 block text-sm font-medium text-text-muted">
            Label <span className="text-text-faint">(optional — defaults to the world name)</span>
          </label>
          <input
            id="account-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={60}
            placeholder="e.g. Main, Mule 2"
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted hover:bg-bg-elevated-hover"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
          >
            {pending ? 'Saving…' : account ? 'Save changes' : 'Create account'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------------------------------
// Character form
// -------------------------------------------------------------------------------------------

interface ClassOption {
  value: string;
  label: string;
}

function CharacterFormModal({
  accountId,
  character,
  onClose,
}: {
  accountId: number;
  character: CharacterDto | null;
  onClose: () => void;
}) {
  const { data: classes } = useClasses();
  const createCharacter = useCreateCharacter();
  const updateCharacter = useUpdateCharacter();
  const refreshAvatar = useRefreshAvatar();
  const [name, setName] = useState(character?.name ?? '');
  const [classSlug, setClassSlug] = useState(character?.classSlug ?? '');
  const [level, setLevel] = useState(String(character?.level ?? 1));
  const [arcaneForce, setArcaneForce] = useState(
    character?.arcaneForce != null ? String(character.arcaneForce) : '',
  );
  const [sacredForce, setSacredForce] = useState(
    character?.sacredForce != null ? String(character.sacredForce) : '',
  );
  const [error, setError] = useState<string | null>(null);
  // REV4: local override so "Refresh avatar" reflects immediately without waiting on the parent's
  // (stale, snapshot-at-open-time) `character` prop to catch up with the invalidated accounts query.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(character?.avatarUrl ?? null);

  const branches = classes ? groupClassesByBranch(classes) : new Map<string, ClassDto[]>();
  const pending = createCharacter.isPending || updateCharacter.isPending;

  const classOptionGroups: readonly GroupBase<ClassOption>[] = useMemo(
    () =>
      [...branches.entries()].map(([branch, klasses]) => ({
        label: branch,
        options: klasses.map((klass) => ({ value: klass.slug, label: klass.name })),
      })),
    [branches],
  );
  const flatClassOptions = useMemo(() => classOptionGroups.flatMap((group) => group.options), [classOptionGroups]);
  const selectedClassOption = flatClassOptions.find((option) => option.value === classSlug) ?? flatClassOptions[0] ?? null;

  // Fuzzy class search via a Fuse index over the 53 class names, handling typos ("bishp" ->
  // Bishop). Fuse's edit-distance fuzziness alone can't cleanly resolve short abbreviation
  // queries like "dk" for "Dark Knight" (either no match, or — once the threshold is loosened
  // enough to catch it — a flood of unrelated classes), so abbreviations are matched separately
  // and deterministically against each class's initials ("Dark Knight" -> "dk"), and the two
  // match sets are unioned. filterOption is invoked once per option per keystroke by react-select,
  // so the search itself is cached per raw query string rather than re-run for every option.
  const classFuse = useMemo(
    () => (classes ? new Fuse(classes, { keys: ['name'], threshold: 0.35, ignoreLocation: true }) : null),
    [classes],
  );
  const fuseQueryCache = useRef<{ query: string; matches: Set<string> }>({ query: '', matches: new Set() });
  const filterClassOption = (option: { value: string; label: string }, rawInput: string) => {
    const query = rawInput.trim();
    if (!query) {
      return true;
    }
    if (!classFuse || !classes) {
      // Fallback if the Fuse index isn't ready yet (classes still loading).
      return option.label.toLowerCase().includes(query.toLowerCase());
    }
    if (fuseQueryCache.current.query !== query) {
      const matches = new Set(classFuse.search(query).map((result) => result.item.slug));
      const lowerQuery = query.toLowerCase();
      for (const klass of classes) {
        const initials = klass.name
          .split(/\s+/)
          .map((word) => word[0])
          .join('')
          .toLowerCase();
        if (initials.length > 1 && initials.startsWith(lowerQuery)) {
          matches.add(klass.slug);
        }
      }
      fuseQueryCache.current = { query, matches };
    }
    return fuseQueryCache.current.matches.has(option.value);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    const selectedClass = classSlug || [...branches.values()][0]?.[0]?.slug || '';
    if (!selectedClass) {
      setError('Pick a class.');
      return;
    }
    const parsedLevel = Number(level);
    if (!Number.isInteger(parsedLevel) || parsedLevel < LEVEL_MIN || parsedLevel > LEVEL_MAX) {
      setError(`Level must be between ${LEVEL_MIN} and ${LEVEL_MAX}.`);
      return;
    }

    const input = {
      name: name.trim(),
      classSlug: selectedClass,
      level: parsedLevel,
      arcaneForce: arcaneForce.trim() === '' ? null : Number(arcaneForce),
      sacredForce: sacredForce.trim() === '' ? null : Number(sacredForce),
    };
    const onError = (err: unknown) => {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not save this character.');
    };

    if (character) {
      updateCharacter.mutate(
        { accountId, characterId: character.id, input },
        { onSuccess: onClose, onError },
      );
    } else {
      createCharacter.mutate({ accountId, input }, { onSuccess: onClose, onError });
    }
  };

  return (
    <Modal title={character ? 'Edit character' : 'Add character'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {character && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover p-3">
            <CharacterAvatar
              name={character.name}
              accountId={character.accountId}
              characterId={character.id}
              avatarUrl={avatarUrl}
              size="modal"
            />
            <div className="flex-1">
              <button
                type="button"
                disabled={refreshAvatar.isPending}
                onClick={() =>
                  refreshAvatar.mutate(
                    { accountId, characterId: character.id },
                    { onSuccess: (updated) => setAvatarUrl(updated.avatarUrl) },
                  )
                }
                className="text-sm font-semibold text-accent hover:underline disabled:opacity-50"
              >
                {refreshAvatar.isPending ? 'Refreshing…' : 'Refresh avatar'}
              </button>
              {refreshAvatar.isSuccess && avatarUrl === null && (
                <p className="mt-1 text-xs text-text-faint">Couldn&rsquo;t find this character on the rankings.</p>
              )}
            </div>
          </div>
        )}
        <div>
          <label htmlFor="char-name" className="mb-1 block text-sm font-medium text-text-muted">
            Name
          </label>
          <input
            id="char-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="char-class" className="mb-1 block text-sm font-medium text-text-muted">
            Class
          </label>
          <Select<ClassOption, false, GroupBase<ClassOption>>
            inputId="char-class"
            instanceId="char-class"
            unstyled
            options={classOptionGroups}
            value={selectedClassOption}
            onChange={(option) => setClassSlug(option?.value ?? '')}
            filterOption={filterClassOption}
            placeholder="Search classes…"
            noOptionsMessage={() => 'No matching classes'}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 60 }) }}
            classNames={selectClassNames}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="char-level" className="mb-1 block text-sm font-medium text-text-muted">
              Level
            </label>
            <input
              id="char-level"
              type="number"
              inputMode="numeric"
              min={LEVEL_MIN}
              max={LEVEL_MAX}
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="char-af" className="mb-1 block text-sm font-medium text-text-muted">
              Arcane
            </label>
            <input
              id="char-af"
              type="number"
              inputMode="numeric"
              min={0}
              value={arcaneForce}
              onChange={(e) => setArcaneForce(e.target.value)}
              placeholder="—"
              className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="char-sf" className="mb-1 block text-sm font-medium text-text-muted">
              Sacred
            </label>
            <input
              id="char-sf"
              type="number"
              inputMode="numeric"
              min={0}
              value={sacredForce}
              onChange={(e) => setSacredForce(e.target.value)}
              placeholder="—"
              className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
          </div>
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted hover:bg-bg-elevated-hover"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
          >
            {pending ? 'Saving…' : character ? 'Save changes' : 'Add character'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------------------------------
// Character row
// -------------------------------------------------------------------------------------------

function CharacterRow({
  account,
  character,
  index,
  count,
  onEdit,
}: {
  account: AccountDto;
  character: CharacterDto;
  index: number;
  count: number;
  onEdit: () => void;
}) {
  const { data: classes } = useClasses();
  const reorder = useReorderCharacters();
  const deleteCharacter = useDeleteCharacter();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const className = classDisplayName(character.classSlug, classes);

  const move = (delta: number) => {
    const ids = account.characters.map((c) => c.id);
    const from = index;
    const to = index + delta;
    if (to < 0 || to >= ids.length) {
      return;
    }
    const reordered = [...ids];
    const [moved] = reordered.splice(from, 1);
    if (moved === undefined) {
      return;
    }
    reordered.splice(to, 0, moved);
    reorder.mutate({ accountId: account.id, characterIds: reordered });
  };

  return (
    <>
      <li className="flex min-h-tap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2">
        <div className="flex shrink-0 flex-col">
          <button
            type="button"
            aria-label={`Move ${character.name} up`}
            disabled={index === 0}
            onClick={() => move(-1)}
            className="flex min-h-tap min-w-tap items-center justify-center text-xs text-text-faint hover:text-accent disabled:opacity-20"
          >
            ▲
          </button>
          <button
            type="button"
            aria-label={`Move ${character.name} down`}
            disabled={index === count - 1}
            onClick={() => move(1)}
            className="flex min-h-tap min-w-tap items-center justify-center text-xs text-text-faint hover:text-accent disabled:opacity-20"
          >
            ▼
          </button>
        </div>

        <CharacterAvatar
          name={character.name}
          accountId={character.accountId}
          characterId={character.id}
          avatarUrl={character.avatarUrl}
          size="row"
        />

        <Link
          to={`/characters/${character.id}`}
          className="flex min-h-tap min-w-0 flex-1 flex-col justify-center rounded-lg hover:bg-bg-elevated"
        >
          <p className="truncate text-sm font-medium text-text underline-offset-2 hover:underline">
            {character.name}
          </p>
          <p className="truncate text-xs text-text-faint">
            {className} · Lv.{character.level}
            {character.arcaneForce != null ? ` · AF ${character.arcaneForce}` : ''}
            {character.sacredForce != null ? ` · SF ${character.sacredForce}` : ''}
          </p>
        </Link>

        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${character.name}`}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-bg-elevated hover:text-text"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          aria-label={`Delete ${character.name}`}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
        >
          <IconTrash />
        </button>
      </li>
      {confirmingDelete && (
        <ConfirmDialog
          title="Delete character"
          message={`Deleting "${character.name}" also deletes every task and completion recorded for them. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          pending={deleteCharacter.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() =>
            deleteCharacter.mutate(
              { accountId: account.id, characterId: character.id },
              { onSuccess: () => setConfirmingDelete(false) },
            )
          }
        />
      )}
    </>
  );
}

// -------------------------------------------------------------------------------------------
// Account card
// -------------------------------------------------------------------------------------------

/** REV7: the drag-handle wiring `SortableAccountCard` hands down — omitted entirely for a
 *  single-account user, since there's nothing to reorder. */
interface AccountDragHandle {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

function AccountCard({ account, dragHandle }: { account: AccountDto; dragHandle?: AccountDragHandle | null }) {
  const { data: worlds } = useWorlds();
  const deleteAccount = useDeleteAccount();
  const [editingAccount, setEditingAccount] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [addingCharacter, setAddingCharacter] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<CharacterDto | null>(null);
  const [importingScreenshot, setImportingScreenshot] = useState(false);
  const [sortingByLevel, setSortingByLevel] = useState(false);
  const [findingDuplicates, setFindingDuplicates] = useState(false);
  const reorder = useReorderCharacters();

  const world = worlds?.find((w) => w.slug === account.worldSlug);

  // Persists through the same PATCH the per-row move buttons use, so the new order sticks
  // everywhere characters are listed (tab strips, overviews). Stable sort: equal levels keep
  // their current relative order.
  const applyLevelSort = (direction: 'desc' | 'asc') => {
    const characterIds = [...account.characters]
      .sort((a, b) => (direction === 'desc' ? b.level - a.level : a.level - b.level))
      .map((c) => c.id);
    reorder.mutate({ accountId: account.id, characterIds }, { onSuccess: () => setSortingByLevel(false) });
  };

  return (
    <section className="card-hover rounded-xl border border-border bg-bg-elevated p-5 transition-colors hover:bg-bg-elevated-hover">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          {dragHandle && (
            <button
              type="button"
              ref={dragHandle.setActivatorNodeRef}
              aria-label={`Reorder ${account.label}`}
              className="flex min-h-tap min-w-tap shrink-0 touch-none cursor-grab items-center justify-center rounded-lg text-text-faint hover:bg-bg-elevated-hover hover:text-text active:cursor-grabbing"
              {...dragHandle.attributes}
              {...dragHandle.listeners}
            >
              <IconGripVertical />
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-base font-semibold text-text">{account.label}</h2>
              {world?.worldType === 'HEROIC' && (
                <span className="rounded-full bg-accent-dim px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-accent-strong">
                  Heroic
                </span>
              )}
            </div>
            <p className="text-xs text-text-faint">{world?.name ?? account.worldSlug}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {account.characters.length > 1 && (
            <button
              type="button"
              onClick={() => setSortingByLevel(true)}
              aria-label={`Sort ${account.label} by level`}
              title="Sort by level"
              className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-bg-elevated-hover hover:text-text"
            >
              <IconSortLevel />
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditingAccount(true)}
            aria-label={`Edit account ${account.label}`}
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-bg-elevated-hover hover:text-text"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            aria-label={`Delete account ${account.label}`}
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
          >
            <IconTrash />
          </button>
        </div>
      </div>

      {account.characters.length > 1 && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => setFindingDuplicates(true)}
            className="min-h-tap rounded-lg px-2 text-xs font-semibold text-text-faint transition-colors hover:bg-bg-elevated-hover hover:text-accent"
          >
            Find duplicate classes
          </button>
        </div>
      )}

      {/* mt-2 when the duplicates row above is present, mt-4 straight under the header otherwise */}
      <ul className={account.characters.length > 1 ? 'mt-2 space-y-2' : 'mt-4 space-y-2'}>
        {account.characters.map((character, index) => (
          <CharacterRow
            key={character.id}
            account={account}
            character={character}
            index={index}
            count={account.characters.length}
            onEdit={() => setEditingCharacter(character)}
          />
        ))}
      </ul>

      {account.characters.length === 0 && (
        <p className="mt-3 text-sm text-text-muted">No characters on this account yet.</p>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => setAddingCharacter(true)}
          className="min-h-tap flex-1 rounded-lg border border-dashed border-border-strong text-sm font-semibold text-text-faint transition-colors hover:border-accent hover:text-accent"
        >
          + Add character
        </button>
        <button
          type="button"
          onClick={() => setImportingScreenshot(true)}
          className="min-h-tap flex-1 rounded-lg border border-dashed border-border-strong text-sm font-semibold text-text-faint transition-colors hover:border-accent hover:text-accent"
        >
          Import from screenshot
        </button>
      </div>

      {findingDuplicates && (
        <DuplicateClassesModal account={account} onClose={() => setFindingDuplicates(false)} />
      )}
      {sortingByLevel && (
        <Modal title="Sort by level" onClose={() => setSortingByLevel(false)}>
          <p className="text-sm text-text-muted">
            Reorder <span className="font-semibold text-text">{account.label}</span>&rsquo;s characters by level.
            This saves the new order — it applies everywhere characters are listed.
          </p>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setSortingByLevel(false)}
              className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated-hover"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reorder.isPending}
              onClick={() => applyLevelSort('asc')}
              className="min-h-tap rounded-lg border border-border-strong px-4 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated-hover disabled:opacity-50"
            >
              Lowest first
            </button>
            <button
              type="button"
              disabled={reorder.isPending}
              onClick={() => applyLevelSort('desc')}
              className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
            >
              {reorder.isPending ? 'Working…' : 'Highest first'}
            </button>
          </div>
        </Modal>
      )}
      {editingAccount && <AccountFormModal account={account} onClose={() => setEditingAccount(false)} />}
      {addingCharacter && (
        <CharacterFormModal accountId={account.id} character={null} onClose={() => setAddingCharacter(false)} />
      )}
      {editingCharacter && (
        <CharacterFormModal
          accountId={account.id}
          character={editingCharacter}
          onClose={() => setEditingCharacter(null)}
        />
      )}
      {importingScreenshot && (
        <Suspense
          fallback={
            <Modal title="Import from screenshot" onClose={() => setImportingScreenshot(false)}>
              <p className="text-sm text-text-muted">Loading…</p>
            </Modal>
          }
        >
          <ScreenshotImportModal account={account} onClose={() => setImportingScreenshot(false)} />
        </Suspense>
      )}
      {confirmingDelete && (
        <ConfirmDialog
          title="Delete account"
          message={`Deleting "${account.label}" removes every character on it and all of their recorded completions. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          pending={deleteAccount.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => deleteAccount.mutate(account.id, { onSuccess: () => setConfirmingDelete(false) })}
        />
      )}
    </section>
  );
}

// -------------------------------------------------------------------------------------------
// Find duplicate classes
// -------------------------------------------------------------------------------------------

/**
 * The hover preview next to a duplicate row's delete button: the Legion loss tweens 0 → −loss in
 * the danger color while the account total tweens current → next. Mounted on hover/focus, so the
 * animation runs each time the preview appears; both figures come from `totalLegionLevel`, so the
 * "loss" honors the real Lv≥60 filter and top-42 cap rather than being blindly −level.
 */
function LegionDeltaPreview({ current, next }: { current: number; next: number }) {
  // Targets start at their "before" values and flip to "after" right past mount — that first
  // post-mount render is what kicks both tweens off.
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(true), []);
  const loss = useTweenedNumber(armed ? next - current : 0);
  const total = useTweenedNumber(armed ? next : current);

  return (
    <div className="shrink-0 text-right">
      <p className="text-sm font-semibold tabular-nums text-danger">{loss.toLocaleString()}</p>
      <p className="text-xs text-text-faint">
        Legion <span className="font-semibold tabular-nums text-text">{total.toLocaleString()}</span>
      </p>
    </div>
  );
}

/**
 * One row in the duplicates cart. Un-staged: a delete button that only STAGES the character (with
 * the hover preview of what committing would do to the staged Legion total). Staged: struck
 * through in the danger tint, with a Restore button that un-stages it. Nothing here mutates —
 * the modal's Commit button is the only thing that deletes.
 */
function DuplicateCharacterRow({
  character,
  className,
  staged,
  currentTotal,
  nextTotal,
  onStage,
  onRestore,
}: {
  character: CharacterDto;
  className: string;
  staged: boolean;
  /** The Legion total with the CURRENT staging applied (not the live total) — so a second hover
   *  after staging one deletion previews from the already-reduced figure, cart-style. */
  currentTotal: number;
  nextTotal: number;
  onStage: () => void;
  onRestore: () => void;
}) {
  const [previewing, setPreviewing] = useState(false);

  return (
    <li
      className={[
        'flex min-h-tap items-center gap-3 rounded-lg border px-3 py-2',
        staged ? 'border-danger/40 bg-danger/10' : 'border-border bg-bg-elevated-hover',
      ].join(' ')}
    >
      <CharacterAvatar
        name={character.name}
        accountId={character.accountId}
        characterId={character.id}
        avatarUrl={character.avatarUrl}
        size="row"
      />
      <div className="min-w-0 flex-1">
        <p
          className={[
            'truncate text-sm font-medium',
            staged ? 'text-text-muted line-through decoration-danger/60' : 'text-text',
          ].join(' ')}
        >
          {character.name}
        </p>
        <p className="truncate text-xs text-text-faint">
          {className} · Lv.{character.level}
        </p>
      </div>
      {staged ? (
        <button
          type="button"
          aria-label={`Restore ${character.name}`}
          onClick={onRestore}
          className="min-h-tap shrink-0 rounded-lg px-3 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated hover:text-text"
        >
          Restore
        </button>
      ) : (
        <>
          {previewing && <LegionDeltaPreview current={currentTotal} next={nextTotal} />}
          <button
            type="button"
            aria-label={`Delete ${character.name}`}
            onClick={onStage}
            onMouseEnter={() => setPreviewing(true)}
            onMouseLeave={() => setPreviewing(false)}
            onFocus={() => setPreviewing(true)}
            onBlur={() => setPreviewing(false)}
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
          >
            <IconTrash />
          </button>
        </>
      )}
    </li>
  );
}

/**
 * Groups the account's characters by class and lists only the classes held by 2+ characters.
 * Shopping-cart semantics: a row's delete button only STAGES the deletion (freely revertible via
 * Restore, or Reset for everything), every figure — the summary bar and each hover preview —
 * reflects the staged state cumulatively, and NOTHING is persisted until the user commits, which
 * runs the same DELETE mutation as the main list once per staged character.
 */
function DuplicateClassesModal({ account, onClose }: { account: AccountDto; onClose: () => void }) {
  const { data: classes } = useClasses();
  const deleteCharacter = useDeleteCharacter();
  const [stagedIds, setStagedIds] = useState<ReadonlySet<number>>(new Set());
  const [confirmingCommit, setConfirmingCommit] = useState(false);
  const [committing, setCommitting] = useState(false);

  const groups = useMemo(() => {
    const bySlug = new Map<string, CharacterDto[]>();
    for (const character of account.characters) {
      bySlug.set(character.classSlug, [...(bySlug.get(character.classSlug) ?? []), character]);
    }
    return [...bySlug.entries()]
      .filter(([, members]) => members.length > 1)
      .map(([slug, members]) => ({ slug, name: classDisplayName(slug, classes), members }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [account.characters, classes]);

  // Intersect staging with the live roster: a character deleted elsewhere (or by a partially
  // failed commit) silently drops out of the cart instead of ghost-counting in the math.
  const staged = account.characters.filter((c) => stagedIds.has(c.id));
  const liveTotal = totalLegionLevel(account.characters);
  const remaining = account.characters.filter((c) => !stagedIds.has(c.id));
  const stagedTotal = totalLegionLevel(remaining);
  // Both summary figures tween whenever staging changes — the running "cart total".
  const displayedLoss = useTweenedNumber(stagedTotal - liveTotal);
  const displayedTotal = useTweenedNumber(stagedTotal);

  const stage = (id: number) => setStagedIds(new Set([...stagedIds, id]));
  const restore = (id: number) => setStagedIds(new Set([...stagedIds].filter((staged) => staged !== id)));

  const commit = async () => {
    setCommitting(true);
    try {
      for (const character of staged) {
        await deleteCharacter.mutateAsync({ accountId: account.id, characterId: character.id });
      }
      setStagedIds(new Set());
      setConfirmingCommit(false);
    } catch {
      // Deletions ran one at a time, so a failure means everything before it IS deleted and
      // everything from it on is untouched; the per-success invalidation has already refreshed the
      // roster, and the intersect above prunes the cart to what actually remains.
      pushToast('Could not delete every staged character — the list shows what actually remains.');
      setConfirmingCommit(false);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Modal title="Duplicate classes" onClose={onClose}>
      {groups.length === 0 ? (
        <p className="text-sm text-text-muted">
          No duplicate classes on {account.label} — every character is a unique class.
        </p>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-text-muted">
            Characters sharing a class on <span className="font-semibold text-text">{account.label}</span> · Legion
            level (approx.){' '}
            <span className="font-semibold tabular-nums text-text">{liveTotal.toLocaleString()}</span>. Deleting only
            stages a character — nothing is removed until you commit.
          </p>
          {groups.map((group) => (
            <div key={group.slug}>
              <p className="font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
                {group.name} · {group.members.length} characters
              </p>
              <ul className="mt-2 space-y-2">
                {group.members.map((character) => (
                  <DuplicateCharacterRow
                    key={character.id}
                    character={character}
                    className={group.name}
                    staged={stagedIds.has(character.id)}
                    currentTotal={stagedTotal}
                    nextTotal={totalLegionLevel(remaining.filter((c) => c.id !== character.id))}
                    onStage={() => stage(character.id)}
                    onRestore={() => restore(character.id)}
                  />
                ))}
              </ul>
            </div>
          ))}

          {staged.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2">
              <p className="text-sm text-text-muted">
                {staged.length} staged ·{' '}
                <span className="font-semibold tabular-nums text-danger">{displayedLoss.toLocaleString()}</span> ·
                Legion{' '}
                <span className="font-semibold tabular-nums text-text">{displayedTotal.toLocaleString()}</span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStagedIds(new Set())}
                  className="min-h-tap rounded-lg px-3 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated-hover"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingCommit(true)}
                  className="min-h-tap rounded-lg bg-danger px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-danger/85"
                >
                  Commit deletions
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {confirmingCommit && (
        <ConfirmDialog
          title="Commit deletions"
          message={`Deleting ${staged.map((c) => `"${c.name}"`).join(', ')} also deletes every task and completion recorded for them. This cannot be undone.`}
          confirmLabel={`Delete ${staged.length} character${staged.length === 1 ? '' : 's'}`}
          danger
          pending={committing}
          onCancel={() => setConfirmingCommit(false)}
          onConfirm={() => void commit()}
        />
      )}
    </Modal>
  );
}

// -------------------------------------------------------------------------------------------
// Drag-to-reorder (REV7)
// -------------------------------------------------------------------------------------------

/**
 * Wraps one {@link AccountCard} in dnd-kit's sortable wiring. The card itself owns no drag state —
 * `useSortable` here supplies a handle-only drag source (`setActivatorNodeRef` + `listeners` go on
 * the grip button `AccountCard` renders, not the whole `<section>`), so text selection, the
 * edit/delete buttons, and page scroll on touch are all unaffected by dragging.
 */
function SortableAccountCard({ account, draggable }: { account: AccountDto; draggable: boolean }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: account.id, disabled: !draggable });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // `data-account-id` gives tests a stable hook to key a mocked getBoundingClientRect off of,
      // since dnd-kit's own collision detection (pointer and keyboard alike) is rect-based.
      data-account-id={account.id}
      className={isDragging ? 'relative z-10 rounded-xl shadow-2xl' : 'relative'}
    >
      <AccountCard
        account={account}
        dragHandle={draggable ? { attributes, listeners, setActivatorNodeRef } : null}
      />
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------------------------

export function Characters() {
  const { data: accounts, isPending, isError } = useAccounts();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const reorderAccounts = useReorderAccounts();
  // A distance threshold so the pointer sensor never fires on a plain tap of the handle — only an
  // actual drag past a few pixels starts the gesture (the handle has no click behaviour of its own,
  // but this keeps touch scroll-vs-drag disambiguation consistent with the keyboard/mouse feel).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!accounts || !over || active.id === over.id) {
      return;
    }
    const oldIndex = accounts.findIndex((a) => a.id === active.id);
    const newIndex = accounts.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const reordered = arrayMove(accounts, oldIndex, newIndex);
    reorderAccounts.mutate(reordered.map((a) => a.id));
  };

  if (isPending) {
    return <p className="text-sm text-text-muted">Loading your roster…</p>;
  }
  if (isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load your roster. Please retry in a moment.
      </p>
    );
  }

  return (
    <div className="stagger-children space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-semibold text-text">Characters</h1>
          <p className="mt-1 text-sm text-text-muted">Accounts and characters you track.</p>
        </div>
        {accounts.length > 0 && (
          <button
            type="button"
            onClick={() => setCreatingAccount(true)}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong"
          >
            + New account
          </button>
        )}
      </div>

      {accounts.length === 0 ? (
        <EmptyState
          title="No accounts yet"
          note="Add your first Maple account and let's get tracking."
          actionOnClick={() => setCreatingAccount(true)}
          actionLabel="Create your first account"
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={accounts.map((account) => account.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-4 lg:grid-cols-2">
              {accounts.map((account) => (
                <SortableAccountCard key={account.id} account={account} draggable={accounts.length > 1} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {creatingAccount && <AccountFormModal account={null} onClose={() => setCreatingAccount(false)} />}
    </div>
  );
}
